import React, { useEffect, useState } from "react";
import {
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

function nightsFromDates(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const s = dayjs(startDate);
  const e = dayjs(endDate);
  if (!s.isValid() || !e.isValid()) return null;
  const diff = e.diff(s, "day");
  return diff > 0 ? diff : null;
}

const ARRIVAL_DOW_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];
const arrivalDowKey = (quoteId) => `qb_quote_${quoteId}_arrival_dow`;

export default function QuoteHeaderForm({ quote, locations, seasons, disabled, onChange, onArrivalDowChange }) {
  const [draft, setDraft] = useState(() => fromQuote(quote));
  const [arrivalDow, setArrivalDow] = useState("");

  // Reset local draft when the quote id changes (server replaced it)
  useEffect(() => {
    setDraft(fromQuote(quote));
  }, [quote.id, quote.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hydrate the placeholder arrival-day-of-week from localStorage (per-quote).
  useEffect(() => {
    if (!quote?.id) return;
    const stored = localStorage.getItem(arrivalDowKey(quote.id));
    setArrivalDow(stored == null ? "" : stored);
    onArrivalDowChange?.(stored == null ? null : Number(stored));
  }, [quote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => {
    let next = { ...draft, ...patch };
    // Whenever both dates are set, nights is derived — overrides any stale value.
    const derived = nightsFromDates(next.startDate, next.endDate);
    if (derived != null) next = { ...next, nights: derived };
    setDraft(next);
    onChange?.(toBody(next));
  };

  const handleArrivalDowChange = (e) => {
    const v = e.target.value;
    setArrivalDow(v);
    if (!quote?.id) return;
    if (v === "") localStorage.removeItem(arrivalDowKey(quote.id));
    else localStorage.setItem(arrivalDowKey(quote.id), v);
    onArrivalDowChange?.(v === "" ? null : Number(v));
  };

  const derivedNights = nightsFromDates(draft.startDate, draft.endDate);
  const nightsLocked = derivedNights != null;
  const serverNights = Number(quote.nights);
  const nightsCorrected = nightsLocked && Number.isFinite(serverNights) && derivedNights !== serverNights;
  const showArrivalDow = !draft.startDate;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>Quote details</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Client Name" required fullWidth size="small"
            disabled={disabled}
            value={draft.clientName}
            onChange={(e) => set({ clientName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Client Email" type="email" fullWidth size="small"
            disabled={disabled}
            value={draft.clientEmail}
            onChange={(e) => set({ clientEmail: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Client Address" fullWidth size="small" multiline minRows={2}
            disabled={disabled}
            value={draft.clientAddress}
            onChange={(e) => set({ clientAddress: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl size="small" fullWidth disabled={disabled}>
            <InputLabel>Season</InputLabel>
            <Select
              label="Season"
              value={draft.seasonId || ""}
              onChange={(e) => set({ seasonId: e.target.value })}
            >
              {seasons.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl size="small" fullWidth disabled={disabled}>
            <InputLabel>Location</InputLabel>
            <Select
              label="Location"
              value={draft.locationId || ""}
              onChange={(e) => set({ locationId: e.target.value })}
            >
              {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            label="Start Date" type="date" fullWidth size="small"
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
            value={draft.startDate}
            onChange={(e) => set({ startDate: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            label="Nights" type="number" fullWidth size="small"
            inputProps={{ min: 1 }}
            disabled={disabled || nightsLocked}
            value={nightsLocked ? derivedNights : draft.nights}
            onChange={(e) => set({ nights: e.target.value })}
            color={nightsCorrected ? "warning" : undefined}
            focused={nightsCorrected || undefined}
            helperText={
              nightsCorrected
                ? `Auto-corrected from ${serverNights} to match the date range`
                : nightsLocked
                  ? "Derived from dates"
                  : "Used when dates aren't set"
            }
            FormHelperTextProps={nightsCorrected ? { sx: { color: "warning.main" } } : undefined}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            label="End Date" type="date" fullWidth size="small"
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
            value={draft.endDate}
            onChange={(e) => set({ endDate: e.target.value })}
          />
        </Grid>
        {!nightsLocked && (
          <Grid item xs={6} md={4}>
            <TextField
              label="Month Hint" type="number" fullWidth size="small"
              inputProps={{ min: 1, max: 12 }}
              disabled={disabled}
              value={draft.monthHint}
              onChange={(e) => set({ monthHint: e.target.value })}
              helperText="Use when dates aren't firm yet"
            />
          </Grid>
        )}
        {showArrivalDow && (
          <Grid item xs={6} md={4}>
            <FormControl size="small" fullWidth disabled={disabled}>
              <InputLabel>Arrival day</InputLabel>
              <Select
                label="Arrival day"
                value={arrivalDow}
                onChange={handleArrivalDowChange}
              >
                <MenuItem value="">(none)</MenuItem>
                {ARRIVAL_DOW_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={String(o.value)}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Student Pax" type="number" fullWidth size="small"
                inputProps={{ min: 0 }}
                disabled={disabled}
                value={draft.studentPax}
                onChange={(e) => set({ studentPax: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Visiting Adult Pax" type="number" fullWidth size="small"
                inputProps={{ min: 0 }}
                disabled={disabled}
                value={draft.visitingAdultPax}
                onChange={(e) => set({ visitingAdultPax: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ height: "100%" }}>
                <Typography variant="body2" color="text.secondary">Residential Bliss staff:</Typography>
                <Chip size="small" label={quote.blissStaffPax ?? 0} />
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Notes" fullWidth size="small" multiline minRows={2}
            disabled={disabled}
            value={draft.notes}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            label="Quote Valid Until" type="date" fullWidth size="small"
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
            value={draft.validityUntil}
            onChange={(e) => set({ validityUntil: e.target.value })}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

function fromQuote(q) {
  return {
    clientName: q.clientName || "",
    clientEmail: q.clientEmail || "",
    clientAddress: q.clientAddress || "",
    seasonId: q.seasonId || "",
    locationId: q.locationId || "",
    monthHint: q.monthHint ?? "",
    startDate: q.startDate || "",
    endDate: q.endDate || "",
    nights: q.nights ?? 1,
    studentPax: q.studentPax ?? 0,
    visitingAdultPax: q.visitingAdultPax ?? 0,
    validityUntil: q.validityUntil || "",
    notes: q.notes || "",
  };
}

function toBody(d) {
  return {
    clientName: d.clientName.trim(),
    clientEmail: d.clientEmail.trim() || null,
    clientAddress: d.clientAddress.trim() || null,
    seasonId: d.seasonId ? Number(d.seasonId) : null,
    locationId: d.locationId ? Number(d.locationId) : null,
    monthHint: d.monthHint === "" ? null : Number(d.monthHint),
    startDate: d.startDate || null,
    endDate: d.endDate || null,
    nights: Number(d.nights) || 1,
    studentPax: Number(d.studentPax) || 0,
    visitingAdultPax: Number(d.visitingAdultPax) || 0,
    validityUntil: d.validityUntil || null,
    notes: d.notes.trim() || null,
  };
}
